
import SortIcon from '@mui/icons-material/Sort'
import AnyCard from 'components/Card'
import { MasonryGrid } from 'components/ContentGridView'
import EmptyView from 'components/EmptyView'
import PPSelect from 'components/PPSelect'
import { ContentItem } from 'interfaces'
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch } from 'react-redux'
import { NavLink, useNavigate, useParams } from 'react-router'
import { renderLoadingCards } from 'routes/find/routes/home/components/utils'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, filter, switchMap, tap } from 'rxjs/operators'
import { checkFeatureAvailability } from 'services/product'
import { searchStockContent } from 'services/search/actions'
import { FEATURE_CONTENT_ON_DEMAND } from 'shared/constants'
import { StoreThunkDispatch } from 'store/state'
import { resetScroll } from 'utils/dom'
import { updateState } from '../state/actions'
import { SearchContext } from '../state/context'
import styles from './SearchRoot.pcss'

export const SearchGoogleRoute = () => {
  const navigate = useNavigate()
  const params = useParams()

  const [{ query, activeFilter }, searchDispatch] = useContext(SearchContext)
  const [loading, setLoading] = useState(false)
  const [sortBy, setSortBy] = useState('rel')
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [items, setItems] = useState<ContentItem[]>([])
  const search$ = useRef<Subject<{ q: string, type: 'articles' | 'videos', sortBy: string }>>(new Subject())
  const scrollElementRef = useRef<HTMLElement | undefined>(undefined)
  const feedName = query.trim()

  useLayoutEffect(() => {
    scrollElementRef.current = document.querySelector('[data-test="main"]') as HTMLElement
    resetScroll()
  }, [])

  useEffect(() => {
    searchDispatch(updateState({ activeFilter: params.type as any, activeCategory: 'content' }))
  }, [params.type, searchDispatch])

  useEffect(() => {
    const sub = search$.current.pipe(filter(({ q }) => Boolean(q))).pipe(
      tap(() => {
        setLoading(true)
        setItems([])
      }),
      switchMap(({ q, type, sortBy }) => {
        return dispatch(searchStockContent(q, type as any, 'google', sortBy)).pipe(catchError((error) => {
          return observableOf({ error })
        }))
      })
    )
      .subscribe((response: any) => {
        setLoading(false)
        if (!response.error) {
          setItems(response.map((item: ContentItem) => ({
            ...item,
            feed: { ...item.feed, name: feedName }
          })))
        }
      })

    return () => {
      sub.unsubscribe()
    }
  }, [dispatch, feedName, sortBy])

  useEffect(() => {
    const hasAccess = dispatch(checkFeatureAvailability(
      FEATURE_CONTENT_ON_DEMAND,
      false,
      () => navigate({ pathname: `/content/search/sources/streams?q=${query}` })
    ))

    if (!hasAccess) {
      return
    }
    if (query) {
      search$.current.next({ q: query, type: activeFilter as any, sortBy })
    }
  }, [query, activeFilter, dispatch, navigate, sortBy])

  const onCompose = useCallback(() => {
    return !dispatch(checkFeatureAvailability(FEATURE_CONTENT_ON_DEMAND))
  }, [dispatch])

  const cards = useMemo(() => {
    const count = 30
    if (loading) {
      return renderLoadingCards(count)
    }

    if (items.length === 0) {
      return renderLoadingCards(count, false, false, false)
    }

    return items.map(item => (
      <AnyCard
        key={item.id}
        content={item}
        children={item}
        onCompose={onCompose}
      />
    ))
  }, [items, loading, onCompose])

  const onSortByChange = (value: string) => {
    setSortBy(value)
  }

  return (
    <div className={`${styles.box} ${styles['top-offset']}`}>
      <header className={styles.header}>
        <h2 className={`text-ellipsis ${styles.title}`}>
          <FormattedMessage id={`search.navigation.${activeFilter}`} />
        </h2>
        <NavLink to={`/content/search?q=${query}`}>
          <FormattedMessage id="general.carousel.nav.back" />
        </NavLink>
        <PPSelect
          name={<FormattedMessage id="label.generic.sort-by" />}
          selectedValue={sortBy}
          icon={<SortIcon />}
          options={{
            rel: { label: 'Relevance' },
            date: { label: 'Recency' }
            // score: { label: 'Stars' }
          }}
          withCaret
          onSelectionChange={onSortByChange}
        />
      </header>
      <MasonryGrid key={query + sortBy}>
        {cards}
      </MasonryGrid>
      {!loading && query && items.length === 0 && (
        <EmptyView
          title={`No ${activeFilter} available for that search`}
          subtitle="Please try a different keyword, topic, brand or industry."
          contained
          top="300px"
        />
      )}
    </div>
  )
}

export default SearchGoogleRoute
